<script lang="ts">
  import type { Snippet } from 'svelte'
  import type { Area } from '../../shared/contract'
  import Lamp from './Lamp.svelte'
  import RunGroups from './RunGroups.svelte'
  import RunItem from './RunItem.svelte'
  import { count, filtering, liftRows, matches, sortRuns, type Filter } from './tally'
  import type { Opening } from './when'

  /**
   * The desktop's right side: every run named, in five balanced columns by
   * area. A filter fades the runs that don't match rather than removing them.
   * Focused on one area: its lifts, then its runs under grade headings, larger.
   * When a short or narrow window can't show every run, the columns scroll.
   */
  let {
    areas,
    filter,
    anyOpen,
    opens,
    focus,
    onall,
    dock,
  }: {
    areas: readonly Area[]
    filter: Filter
    anyOpen: boolean
    /** Opening day, while it is still ahead. */
    opens: Opening | null
    focus: Area | null
    onall: () => void
    dock: Snippet
  } = $props()

  const f = $derived(filtering(filter))
  const scope = $derived(focus ? focus.runs : areas.flatMap((a) => a.runs))
  const c = $derived(count(scope))
  const groomed = $derived(scope.filter((r) => r.groomed).length)
  const matched = $derived(scope.filter((r) => matches(r, filter)).length)
</script>

<section id="board" class="board" class:focus={!!focus} aria-label={focus ? `${focus.name} runs` : 'All runs'}>
  <div class="bh">
    <div class="ttl">
      <span class="mono">{focus ? focus.name : 'All runs'}</span>
      <span class="cnt">
        {#if f}
          <b>{matched}</b> of {c.total} match
        {:else if anyOpen}
          <b>{c.open}</b> of {c.total} open · <b>{groomed}</b> groomed
        {:else}
          {c.total} runs{opens ? ` · opens ${opens.when}` : ''}
        {/if}
      </span>
    </div>
    {#if focus}<button class="back" onclick={onall}>All areas</button>{/if}
    {@render dock()}
  </div>
  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <div class="cols" tabindex="0" role="region" aria-label="Runs">
    <div class="flow">
    {#if focus}
      <ul class="fl" aria-label="Lifts">
        {#each liftRows(focus.lifts) as lift}
          <li class="lift" class:on={lift.turning} aria-label={lift.label} title={lift.label}>
            <Lamp on={lift.turning} /><span>{lift.name}</span>
            {#if lift.status}<span class="hold mono">{lift.status}</span>{/if}
          </li>
        {:else}
          <li class="lift"><span class="mono">No lifts</span></li>
        {/each}
      </ul>
      <RunGroups runs={focus.runs} {filter} {anyOpen} fade />
    {:else}
      {#each areas as area}
        {@const ac = count(area.runs)}
        <div class="area">
          <h3 class="mono">
            {area.name}
            <span>
              {f
                ? area.runs.filter((r) => matches(r, filter)).length
                : anyOpen
                  ? `${ac.open}/${ac.total}`
                  : ac.total}
            </span>
          </h3>
          <ul>
            {#each sortRuns(area.runs) as run}
              <RunItem {run} ghost={f && !matches(run, filter)} />
            {/each}
          </ul>
        </div>
      {/each}
    {/if}
    </div>
  </div>
</section>

<style>
  .board {
    grid-column: 2;
    grid-row: 1 / 4;
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: var(--glass);
    border: 1px solid var(--glass-edge);
    border-radius: 22px;
    padding: 14px 22px 0;
    container: board / inline-size;
  }
  /* A narrow board puts the filter on its own row under the title. */
  .bh {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
    flex: none;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--line);
  }
  .ttl {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  .ttl .mono {
    color: var(--ink-2);
  }
  .cnt {
    color: var(--ink-2);
    font-size: 15px;
    white-space: nowrap;
  }
  .cnt b {
    color: var(--ink);
    font-weight: 400;
  }
  .back {
    color: var(--ink-2);
    font-size: 13px;
    height: 44px;
    padding: 0 12px;
    border-radius: 10px;
    box-shadow: inset 0 0 0 1px var(--line);
    margin-left: 4px;
    white-space: nowrap;
  }
  .back:hover {
    color: var(--ink);
  }
  .cols {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: 0 -22px;
    padding: 12px 22px 20px;
    border-radius: 0 0 22px 22px;
    mask-image: linear-gradient(#000 calc(100% - 20px), transparent);
  }
  .cols:focus-visible {
    outline-offset: -2px;
  }
  .flow {
    columns: 150px 5;
    column-gap: 14px;
    column-fill: balance;
  }
  .area {
    margin-bottom: 10px;
  }
  h3 {
    letter-spacing: 0.08em;
    padding: 6px 0 5px;
    break-after: avoid;
    color: var(--ink-2);
    display: flex;
    gap: 8px;
    align-items: baseline;
  }
  h3 span {
    color: var(--ink-3);
  }
  .area:first-child h3 {
    padding-top: 0;
  }
  .focus .flow {
    columns: 180px 4;
  }
  .fl {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 22px;
    padding: 2px 0 12px;
    margin-bottom: 4px;
    break-after: avoid;
    column-span: all;
    border-bottom: 1px solid var(--line);
  }
  .lift {
    display: flex;
    align-items: center;
    gap: 9px;
    color: var(--ink-3);
    font-size: 15px;
    height: 26px;
  }
  .lift.on {
    color: var(--ink);
  }
  .hold {
    color: var(--ink-2);
    letter-spacing: 0.08em;
  }
</style>
